import React from 'react';
import { CheckCircle, Clock, XCircle, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { BookingDetails } from '@/hooks/booking/useBookingDetails';

interface BookingTimelineProps {
  booking: BookingDetails;
}

interface TimelineItem {
  status: string;
  icon: React.ReactNode;
  title: string;
  description?: string;
  timestamp?: string;
  isCompleted: boolean;
  isCurrentStep: boolean;
}

export const BookingTimeline = ({ booking }: BookingTimelineProps) => {
  const getTimelineItems = (): TimelineItem[] => {
    const items: TimelineItem[] = [
      {
        status: 'created',
        icon: <Calendar className="h-4 w-4" />,
        title: 'Booking Created',
        description: 'Customer submitted booking request',
        timestamp: booking.created_at,
        isCompleted: true,
        isCurrentStep: false,
      },
    ];

    // Add status-specific timeline items
    switch (booking.status?.toLowerCase()) {
      case 'pending':
        items.push({
          status: 'pending',
          icon: <Clock className="h-4 w-4" />,
          title: 'Awaiting Confirmation',
          description: 'Waiting for vendor response',
          isCompleted: false,
          isCurrentStep: true,
        });
        break;

      case 'accepted':
        items.push(
          {
            status: 'accepted',
            icon: <CheckCircle className="h-4 w-4" />,
            title: 'Booking Confirmed',
            description: 'Vendor accepted the booking',
            timestamp: booking.updated_at,
            isCompleted: true,
            isCurrentStep: false,
          },
          {
            status: 'scheduled',
            icon: <Clock className="h-4 w-4" />,
            title: 'Service Scheduled',
            description: 'Awaiting service completion',
            isCompleted: false,
            isCurrentStep: true,
          }
        );
        break;

      case 'completed':
        items.push(
          {
            status: 'accepted',
            icon: <CheckCircle className="h-4 w-4" />,
            title: 'Booking Confirmed',
            description: 'Vendor accepted the booking',
            isCompleted: true,
            isCurrentStep: false,
          },
          {
            status: 'completed',
            icon: <CheckCircle className="h-4 w-4" />,
            title: 'Service Completed',
            description: booking.completion_notes || 'Service successfully completed',
            timestamp: booking.updated_at,
            isCompleted: true,
            isCurrentStep: false,
          }
        );
        break;

      case 'cancelled':
        items.push({
          status: 'cancelled',
          icon: <XCircle className="h-4 w-4" />,
          title: 'Booking Cancelled',
          description: 'Customer cancelled the booking',
          timestamp: booking.updated_at,
          isCompleted: true,
          isCurrentStep: false,
        });
        break;

      case 'declined':
        items.push({
          status: 'declined',
          icon: <XCircle className="h-4 w-4" />,
          title: 'Booking Declined',
          description: booking.decline_reason || 'Vendor declined the booking',
          timestamp: booking.updated_at,
          isCompleted: true,
          isCurrentStep: false,
        });
        break;
    }

    return items;
  };

  const timelineItems = getTimelineItems();

  return (
    <div className="bg-card rounded-lg border p-6">
      <h2 className="text-lg font-semibold mb-4">Booking Timeline</h2>
      
      <div className="space-y-4">
        {timelineItems.map((item, index) => (
          <div key={item.status} className="flex gap-4">
            {/* Icon and Line */}
            <div className="flex flex-col items-center">
              <div className={`
                flex items-center justify-center w-8 h-8 rounded-full border-2
                ${item.isCompleted 
                  ? 'bg-green-500/10 border-green-500 text-green-600 dark:text-green-400' 
                  : item.isCurrentStep
                    ? 'bg-blue-500/10 border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'bg-gray-500/10 border-gray-300 text-gray-400'
                }
              `}>
                {item.icon}
              </div>
              
              {/* Connecting line */}
              {index < timelineItems.length - 1 && (
                <div className={`
                  w-0.5 h-6 mt-2
                  ${item.isCompleted ? 'bg-green-500/30' : 'bg-gray-300 dark:bg-gray-600'}
                `} />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 pb-4">
              <div className="flex items-center justify-between">
                <h3 className={`
                  font-medium
                  ${item.isCompleted 
                    ? 'text-foreground' 
                    : item.isCurrentStep
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-muted-foreground'
                  }
                `}>
                  {item.title}
                </h3>
                
                {item.timestamp && (
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(item.timestamp), 'MMM d, h:mm a')}
                  </span>
                )}
              </div>
              
              {item.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {item.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};